/**
 * Tax Collectors + Tax Categories admin API client.
 * Phase 3 UI — Surfaces 1 & 2 (see /app/memory/design_tax_collectors_ui.md).
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeader = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('access_token');
  return { Authorization: `Bearer ${token}` };
};

// ── Tax Collectors ───────────────────────────────────────────────

export async function listTaxCollectors(params = {}) {
  const res = await axios.get(`${API}/tax-collectors`, {
    params: { include_counts: true, ...params },
    headers: authHeader(),
  });
  return res.data;
}

export async function getTaxCollector(id) {
  const res = await axios.get(`${API}/tax-collectors/${id}`, { headers: authHeader() });
  return res.data;
}

export async function createTaxCollector(body) {
  const res = await axios.post(`${API}/tax-collectors`, body, { headers: authHeader() });
  return res.data;
}

export async function updateTaxCollector(id, body) {
  const res = await axios.put(`${API}/tax-collectors/${id}`, body, { headers: authHeader() });
  return res.data;
}

export async function deactivateTaxCollector(id) {
  const res = await axios.delete(`${API}/tax-collectors/${id}`, { headers: authHeader() });
  return res.data;
}

export async function listTaxCollectorTypes() {
  const res = await axios.get(`${API}/tax-collectors/_meta/types`, { headers: authHeader() });
  return res.data;
}

// ── Tax Categories ───────────────────────────────────────────────

export async function listTaxCategories(params = {}) {
  const res = await axios.get(`${API}/tax-categories`, {
    params: { include_counts: true, ...params },
    headers: authHeader(),
  });
  return res.data;
}

export async function createTaxCategory(body) {
  const res = await axios.post(`${API}/tax-categories`, body, { headers: authHeader() });
  return res.data;
}

export async function updateTaxCategory(id, body) {
  const res = await axios.put(`${API}/tax-categories/${id}`, body, { headers: authHeader() });
  return res.data;
}

export async function deactivateTaxCategory(id) {
  const res = await axios.delete(`${API}/tax-categories/${id}`, { headers: authHeader() });
  return res.data;
}

// ── Jurisdiction → Collector Assignments (Surface 3) ──────────────

export async function listJurisdictionAssignments(jurisdictionId, params = {}) {
  const res = await axios.get(`${API}/jurisdiction-collector-assignments`, {
    params: { jurisdiction_id: jurisdictionId, active_only: false, ...params },
    headers: authHeader(),
  });
  return res.data;
}

export async function createJurisdictionAssignment(body) {
  const res = await axios.post(`${API}/jurisdiction-collector-assignments`, body, {
    headers: authHeader(),
  });
  return res.data;
}

export async function updateJurisdictionAssignment(id, body) {
  const res = await axios.put(`${API}/jurisdiction-collector-assignments/${id}`, body, {
    headers: authHeader(),
  });
  return res.data;
}

export async function deactivateJurisdictionAssignment(id) {
  const res = await axios.delete(`${API}/jurisdiction-collector-assignments/${id}`, {
    headers: authHeader(),
  });
  return res.data;
}

// ── Collector Guidance — Surface 4 auto-resolve ───────────────────

export async function resolveCollectorGuidance(items, as_of = null) {
  const res = await axios.post(
    `${API}/collector-guidance/resolve`,
    { items, as_of },
    { headers: authHeader() },
  );
  return res.data;
}

// ── Collector Correction Requests — Surfaces 4 + 5 ────────────────

export async function submitCorrectionRequest(body) {
  const res = await axios.post(`${API}/collector-correction-requests`, body, {
    headers: authHeader(),
  });
  return res.data;
}

export async function listCorrectionRequests(params = {}) {
  const res = await axios.get(`${API}/collector-correction-requests`, {
    params,
    headers: authHeader(),
  });
  return res.data;
}

export async function listMyCorrectionRequests(params = {}) {
  const res = await axios.get(`${API}/collector-correction-requests/mine`, {
    params,
    headers: authHeader(),
  });
  return res.data;
}

export async function getCorrectionRequest(id) {
  const res = await axios.get(`${API}/collector-correction-requests/${id}`, {
    headers: authHeader(),
  });
  return res.data;
}

export async function approveCorrectionRequest(id, body = {}) {
  const res = await axios.put(
    `${API}/collector-correction-requests/${id}/approve`,
    body, { headers: authHeader() },
  );
  return res.data;
}

export async function rejectCorrectionRequest(id, review_notes) {
  const res = await axios.put(
    `${API}/collector-correction-requests/${id}/reject`,
    { review_notes }, { headers: authHeader() },
  );
  return res.data;
}

export async function markDuplicateCorrectionRequest(id, body) {
  const res = await axios.put(
    `${API}/collector-correction-requests/${id}/mark-duplicate`,
    body, { headers: authHeader() },
  );
  return res.data;
}

/**
 * Resolve collector defaults (MLC fee · SASE · ETA · instructions) for
 * a (jurisdiction, collector) pair. Implements the precedence cascade:
 *   1. jurisdiction.mlc_charges (override) wins
 *   2. tax_collectors.default_* fallback
 *   3. unset → triggers Mail-Away blocker upstream
 *
 * Returns the ResolvedDefaults bundle:
 *   {
 *     mlc_fee:              { value, provenance },
 *     sase_required:        { value, provenance },
 *     eta_days:             { value, provenance },
 *     request_instructions: { value, provenance },
 *   }
 *
 * Used by:
 *   • TaxCollectorPicker — row chips
 *   • JurisdictionManager — Contact Info Operational Defaults strip
 *   • MailAwayModal — read-only fee preview + Instructions callout
 */
export async function resolveCollectorDefaults({ jurisdictionId, collectorId }) {
  const url = `${API}/tax-collectors/_resolve-defaults`;
  const res = await axios.post(
    url,
    { jurisdiction_id: jurisdictionId, collector_id: collectorId },
    { headers: authHeader() },
  );
  return res.data;
}


/** PA-COLLECTOR-AUTOMATION-001 P2 — resolve a pa_auto_assign review item. */
export async function resolveCorrectionRequest(id, body) {
  const res = await axios.put(
    `${API}/collector-correction-requests/${id}/resolve`,
    body, { headers: authHeader() },
  );
  return res.data;
}
